import { EpihubQueryProvider } from '@epihub/web/src/query';
import * as React from 'react';
import { EpihubRouter } from '@epihub/web/src/routes/Router';
import { Outlet } from 'react-router';
import { WaveText } from '@epihub/web/src/components/WaveText';
import { useEffect, useState } from 'react';
import { useGlobalStore } from './store/useGlobalStore';
import { ErrorBoundaryFallback, rootErrorBoundaryProps } from './components/ErrorBoundary';
import { queryClient } from './_ctx/queryClient';
import { HomeChatScreen } from './screens/HomeChatScreen';
import { GlobalNotFound } from './components/GlobalNotFound';
import { useStreamingResponseListener } from './hooks/useStreamingResponseListener';
import { CalendarDrawer } from './components/calendar/CalendarDrawer';
import { KeyboardContextProvider } from './components/chat/KeyboardContext';
import { ChatViewMessageBoxAnimationContextProvider } from './components/chat/ChatViewMessageBoxAnimationContext';
import { styled, ThemeProvider, useTheme } from './theme/theme';
import { cssFillHeightByPercentage, cssGlobal } from './components/css';
import { useActionListener, useActionLocationListener } from './hooks/useActionListener';
import { useLinkInterceptor } from './hooks/useLinkInterceptor';
import { BookCalendarScreen } from './screens/BookCalendarScreen';
import { useInitializeVisitor } from './hooks/useInitializeVisitor';
import { PrivacyScreen } from './screens/PrivacyScreen';
import { CookieScreen } from './screens/CookieScreen';
import { CVScreen } from './screens/CVScreen';

export const App = () => (
  <ThemeProvider
    useGlobalStore={useGlobalStore}
    errorBoundaryProps={rootErrorBoundaryProps}
  >
    <EpihubQueryProvider
      client={queryClient}
      normalizerConfig={{ getArrayType: ({ arrayKey }) => arrayKey }}
    >
      <EpihubApp />
    </EpihubQueryProvider>
  </ThemeProvider>
);

const EpihubApp = () => {
  return (
    <KeyboardContextProvider>
      <ChatViewMessageBoxAnimationContextProvider>
        <RootView>
          <EpihubRouter
            routes={[{
              element: <CommonRouted />,
              ErrorBoundary: ErrorBoundaryFallback,
              children: [
                {
                  element: <CommonFetchPrepare />,
                  children: [
                    HomeChatScreen(),
                  ],
                },
                BookCalendarScreen(),
                PrivacyScreen(),
                CookieScreen(),
                CVScreen(),
                {
                  path: '/error',
                  element: <ErrorBoundaryFallback />,
                },
                {
                  path: '*',
                  element: <GlobalNotFound />,
                },
              ],
            }]}
          />
          <CalendarDrawer />
        </RootView>
      </ChatViewMessageBoxAnimationContextProvider>
    </KeyboardContextProvider>
  );
};

const CommonRouted = () => {
  useActionLocationListener();
  useLinkInterceptor();
  return <Outlet />;
};

const CommonFetchPrepare = () => {
  const { hasInitiallyLoaded } = useInitializeVisitor();

  const [animationCompleted, setAnimationCompleted] = useState(false);
  const ANIMATION_DURATION = 2000;
  useEffect(() => {
    setTimeout(() => setAnimationCompleted(true), ANIMATION_DURATION);
  }, []);

  const theme = useTheme();

  if (!hasInitiallyLoaded || !animationCompleted) {
    return (
      <WaveText
        duration={2000}
        text='KRISHAN MADAN'
        fontSize={`${theme.screen.widthRange(2, 6)}rem`}
      />
    );
  }
  return <CommonFetch />;
};

const CommonFetch = () => {
  useStreamingResponseListener();
  useActionListener();
  return <Outlet />;
};

const RootView = styled.div`
  ${cssGlobal};
  ${cssFillHeightByPercentage};
  overflow: hidden;
`;
